import { memo, useMemo } from 'react';
import { Property } from '@/types/property';
import { PropertyCard } from './PropertyCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, SearchX } from 'lucide-react';
import { useActivePropertyAds } from '@/hooks/usePropertyAds';

interface PropertyGridProps {
  properties: (Property & { _distance?: number })[] | undefined;
  isLoading: boolean;
  emptyMessage?: string;
  /** True when the query failed (timeout, network, etc.) */
  isError?: boolean;
  /** Retry handler shown with the error state */
  onRetry?: () => void;
  /** True while refreshing in the background with results already on screen */
  isFetching?: boolean;
  /** Optional action rendered under the empty state (e.g. clear filters) */
  emptyAction?: React.ReactNode;
}

export function PropertyGrid({
  properties,
  isLoading,
  emptyMessage = 'No properties found',
  isError,
  onRetry,
  isFetching,
  emptyAction,
}: PropertyGridProps) {
  // Fetch ads ONCE at grid level, not per-card
  const { data: activeAds } = useActivePropertyAds();

  // Build a Set for O(1) lookup
  const adsByPropertyId = useMemo(() => {
    if (!activeAds) return new Map<string, { campaign_type: string }>();
    const map = new Map<string, { campaign_type: string }>();
    for (const ad of activeAds) {
      map.set(ad.property_id, ad);
    }
    return map;
  }, [activeAds]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <PropertyCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-16 px-4">
        <RefreshCw className="w-8 h-8 mx-auto mb-4 text-muted-foreground" />
        <h3 className="font-medium text-lg mb-1">We couldn't load listings just now</h3>
        <p className="text-muted-foreground mb-5 text-sm">
          The search took too long to respond. Please try again in a moment.
        </p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline">
            Try again
          </Button>
        )}
      </div>
    );
  }

  if (!properties || properties.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <SearchX className="w-8 h-8 mx-auto mb-4 text-muted-foreground" />
        <h3 className="font-medium text-lg mb-1">No homes match this search</h3>
        <p className="text-muted-foreground mb-5 text-sm">{emptyMessage}</p>
        {emptyAction}
      </div>
    );
  }

  return (
    <div className="relative">
      {isFetching && (
        <div className="absolute -top-10 right-0 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Updating results…</span>
        </div>
      )}
      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start transition-opacity ${
          isFetching ? 'opacity-60' : 'opacity-100'
        }`}
      >
        {properties.map((property, index) => (
          <PropertyCard
            key={property.id}
            property={property}
            priority={index < 6}
            adType={adsByPropertyId.get(property.id)?.campaign_type as any}
            distanceKm={property._distance}
          />
        ))}
      </div>
    </div>
  );
}

function PropertyCardSkeleton() {
  return (
    <div className="bg-card rounded-lg overflow-hidden shadow-property" style={{ contain: 'layout style' }}>
      <Skeleton className="aspect-[16/10] bg-muted" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex gap-4">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="pt-3 border-t space-y-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    </div>
  );
}
